import React, { useState, useEffect } from 'react';
import { Bell, AlertTriangle, FileWarning, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAlerts, AlertasDashboard } from '@/hooks/useAlerts';
import { useNavigate } from 'react-router-dom';

export const NotificationsDropdown: React.FC = () => {
  const [alerts, setAlerts] = useState<AlertasDashboard | null>(null);
  const { loading, obterAlertasDashboard } = useAlerts();
  const navigate = useNavigate();

  useEffect(() => {
    const loadAlerts = async () => {
      const data = await obterAlertasDashboard();
      setAlerts(data);
    };
    loadAlerts();
  }, [obterAlertasDashboard]);

  const totalAlerts = alerts
    ? alerts.total_inspecoes_atrasadas +
      alerts.total_relatorios_criticos +
      alerts.total_relatorios_prazo_vencido +
      alerts.total_relatorios_sem_indicadores
    : 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="w-5 h-5" />
          {totalAlerts > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center">
              {totalAlerts > 9 ? '9+' : totalAlerts}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>Alertas do Sistema</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {loading ? (
          <div className="p-4 text-center text-muted-foreground text-sm">
            Carregando...
          </div>
        ) : totalAlerts === 0 ? (
          <div className="p-4 text-center text-muted-foreground text-sm">
            Nenhum alerta no momento
          </div>
        ) : (
          <>
            {alerts && alerts.total_inspecoes_atrasadas > 0 && (
              <DropdownMenuItem
                className="flex items-center gap-3 p-3 cursor-pointer"
                onClick={() => navigate('/schedule')}
              >
                <div className="w-8 h-8 rounded-lg bg-warning/10 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-warning" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Inspeções Atrasadas</p>
                  <p className="text-xs text-muted-foreground">
                    {alerts.total_inspecoes_atrasadas} inspeção(ões) pendente(s)
                  </p>
                </div>
              </DropdownMenuItem>
            )}
            {alerts && alerts.total_relatorios_criticos > 0 && (
              <DropdownMenuItem
                className="flex items-center gap-3 p-3 cursor-pointer"
                onClick={() => navigate('/governanca')}
              >
                <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-destructive" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Relatórios Críticos</p>
                  <p className="text-xs text-muted-foreground">
                    {alerts.total_relatorios_criticos} relatório(s) crítico(s)
                  </p>
                </div>
              </DropdownMenuItem>
            )}
            {alerts && alerts.total_relatorios_prazo_vencido > 0 && (
              <DropdownMenuItem
                className="flex items-center gap-3 p-3 cursor-pointer"
                onClick={() => navigate('/governanca')}
              >
                <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center">
                  <FileWarning className="w-4 h-4 text-destructive" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Prazo Vencido</p>
                  <p className="text-xs text-muted-foreground">
                    {alerts.total_relatorios_prazo_vencido} relatório(s) com prazo vencido
                  </p>
                </div>
              </DropdownMenuItem>
            )}
            {alerts && alerts.total_relatorios_sem_indicadores > 0 && (
              <DropdownMenuItem
                className="flex items-center gap-3 p-3 cursor-pointer"
                onClick={() => navigate('/indicators')}
              >
                <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                  <FileWarning className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Sem Indicadores</p>
                  <p className="text-xs text-muted-foreground">
                    {alerts.total_relatorios_sem_indicadores} relatório(s) sem consolidar
                  </p>
                </div>
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationsDropdown;
